import React from "react";
import {
  Box,
  Typography,
  Divider,
  useTheme,
  Button,
  Alert,
} from "@mui/material";
import OrbLoader from "../../components/common/OrbLoader";
import { useTranslation } from "react-i18next";
import { useParams, useLocation } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useSelector } from "react-redux";
import { selectSidebarCollapsed } from "../../store/slices/app/appSelector";
import IOMarginTable from "../../components/table/IOMarginTable";
import IODbMarginTable from "../../components/table/IODbMarginTable";
import {
  useGetIOMarginsByInsertionOrderIdQuery,
  useGetRawIOMarginPeriodsQuery,
} from "../../store/apis/insertionOrderMarginApi";
import { useGetInsertionOrderByIdQuery } from "../../store/apis/insertionOrderApi";
import { useGetInternalCampaignByIdQuery } from "../../store/apis/internalCampaignApi";
import PageBreadcrumbs from "../../components/layout/PageBreadcrumbs";
import { useGoBack } from "../../hooks/useGoBack";

interface LocationState {
  insertionOrderName?: string;
}

const IOMargins: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const goBack = useGoBack();
  const { insertionOrderId } = useParams<{ insertionOrderId: string }>();
  const location = useLocation();
  const { insertionOrderName: nameFromLocation } =
    (location.state as LocationState) || {};
  const collapsed = useSelector(selectSidebarCollapsed);

  // Optionally fetch IO details to get a display name when not passed via navigation
  const {
    data: ioDetails,
    isLoading: isLoadingIO,
    error: ioError,
  } = useGetInsertionOrderByIdQuery(insertionOrderId || "", {
    skip: !insertionOrderId,
  });

  const ioName = nameFromLocation || ioDetails?.data?.displayName;

  // Determine related internal campaign (take the first link for now)
  const internalCampaignId =
    ioDetails?.data?.internalCampaignLinks &&
    ioDetails.data.internalCampaignLinks.length > 0
      ? ioDetails.data.internalCampaignLinks[0].internalCampaignId
      : undefined;

  // Fetch internal campaign to obtain its start/end date to restrict margin periods
  const { data: internalCampaignRes } = useGetInternalCampaignByIdQuery(
    internalCampaignId || "",
    { skip: !internalCampaignId }
  );
  const icStartDate = internalCampaignRes?.data?.startDate || null;
  const icEndDate = internalCampaignRes?.data?.endDate || null;

  const handleBack = () => {
    goBack("/metrics");
  };

  // Fetch consolidated (computed) margins for this IO
  const {
    data: consolidatedRes,
    isLoading: isLoadingConsolidated,
    error: consolidatedError,
  } = useGetIOMarginsByInsertionOrderIdQuery(insertionOrderId || "", {
    skip: !insertionOrderId,
  });

  // Fetch raw margin data from the database
  const {
    data: rawRes,
    isLoading: isLoadingRaw,
    error: rawError,
  } = useGetRawIOMarginPeriodsQuery(insertionOrderId || "", {
    skip: !insertionOrderId,
  });

  const consolidatedMargins = consolidatedRes?.data || [];
  const rawMargins = rawRes?.data || [];

  return (
    <Box sx={{ display: "flex", minHeight: "100vh" }}>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: { xs: 2, sm: 3 },
          transition: "margin-left 0.3s ease",
          marginLeft: collapsed ? "64px" : "250px",
          display: "flex",
          flexDirection: "column",
          width: { xs: "calc(100% - 64px)", sm: "auto" },
          maxWidth: "100%",
          boxSizing: "border-box",
          overflowX: "hidden",
          minHeight: "100vh",
        }}
      >
        <PageBreadcrumbs />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 2, sm: 0 },
          }}
        >
          <Box>
            <Typography variant="h3" gutterBottom>
              {t("metrics.ioMargins", "Insertion Order Margins")}
            </Typography>
            {ioName && (
              <Typography
                variant="pSmall"
                gutterBottom
                color={theme.palette.neutral[600]}
              >
                {t("metrics.ioName", "Insertion Order: {{name}}", {
                  name: ioName,
                })}
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-start",
            alignItems: "center",
            mt: 2,
            mb: 2,
          }}
        >
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={handleBack}
            variant="outlined"
            sx={{
              borderRadius: 50,
              textTransform: "none",
              fontFamily: "Nunito",
              borderColor: theme.palette.grey[200],
              color: theme.palette.grey[700],
            }}
          >
            {t("common.back", "Back")}
          </Button>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 3 }} />

          <Typography variant="h5" sx={{ mb: 2 }}>
            {t("metrics.calculatedMargins", "Calculated Margins")}
          </Typography>

          {isLoadingConsolidated ? (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <OrbLoader
                size="large"
                label={t("common.loading", { defaultValue: "Cargando…" })}
              />
            </Box>
          ) : consolidatedError ? (
            <Typography color="error">
              {t("common.errorLoading", "Error loading data")}
            </Typography>
          ) : (
            <IOMarginTable margins={consolidatedMargins} />
          )}

          <Divider sx={{ my: 4 }} />

          {ioError ? (
            <Alert severity="error" sx={{ mt: 2 }}>
              {t(
                "errors.failedToLoadInsertionOrder",
                "Failed to load insertion order details"
              )}
            </Alert>
          ) : (
            <>
              {rawError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {t("common.errorLoading", "Error loading data")}
                </Alert>
              )}
              <IODbMarginTable
                rawMargins={rawMargins}
                isLoading={isLoadingRaw || isLoadingIO}
                insertionOrderId={insertionOrderId || ""}
                startDate={icStartDate}
                endDate={icEndDate}
              />
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default IOMargins;
